import { StandardEvents } from '@shopify/events';

/**
 * Keeps Prüvit's per-variant content in step with the variant picker.
 *
 * Horizon does not re-render custom blocks on a variant change. By default the
 * picker morphs only itself and fires a bubbling productSelect event; the price,
 * gallery and buy buttons update because they listen for it. Anything rendered
 * from Liquid and left alone keeps showing the first variant's content — which
 * for the supplement panel means the wrong label, so this is a correctness
 * fix, not a polish one.
 *
 * Two things are kept in step:
 *   - the gallery ground, from the flavour's ground_color metaobject field
 *   - the supplement panel, from the variant's custom.supplement_label
 *
 * Only the first of those needs a flavour, which is why this is a variant sync
 * rather than the flavour sync it was called until 2026-09-18. K_1 is the only
 * product with a flavour option; the KETO//OS products have none, and their
 * Charged and Caffeine Free panels are two different images. So the panel
 * follows whichever axes a product has and the ground is set only where there
 * is a flavour to have one.
 */
class PruvitVariantSync extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('script[type="application/json"]');
    try {
      this.data = json ? JSON.parse(json.textContent) : null;
    } catch {
      this.data = null;
    }
    if (!this.data) return;

    // Scope to this product's section so a quick-add modal elsewhere on the
    // page cannot drive this one.
    this.scope = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]') || document;
    this.scope.addEventListener(StandardEvents.productSelect, this.handleSelect);

    // Liquid puts the first variant's ground on this element, but the gallery is
    // a sibling rather than a child, so it has to be lifted to the section for
    // anything to inherit it.
    const initial = this.style.getPropertyValue('--flavor-ground').trim();
    if (initial) this.setGround(initial);
  }

  disconnectedCallback() {
    this.scope?.removeEventListener(StandardEvents.productSelect, this.handleSelect);
  }

  handleSelect = (event) => {
    const options = event.selectedOptions;
    if (!Array.isArray(options)) return;

    /**
     * The picker reports option names in the page's language, so looking for
     * "flavor" found nothing in any of the eight non-English locales, apply()
     * returned early on the empty flavour, and the panel kept the first
     * variant's label — the fault this block exists to prevent, reintroduced
     * by the lookup rather than by the block. Liquid knows which axis is which
     * and says so in `roles`.
     */
    const read = (...wanted) => {
      const names = wanted.filter(Boolean).map((n) => n.toLowerCase());
      const hit = options.find((o) => names.includes((o?.name || '').toLowerCase()));
      return hit ? String(hit.value ?? '') : '';
    };

    // A `roles` key means Liquid has already decided which axis is which, and
    // an empty flavour there is an answer — the product has no flavour option
    // — not a gap to fill with a guess at the English name. The English
    // fallback is only for markup cached from before this was emitted.
    const roles = this.data.roles;
    if (roles) this.apply(read(roles.flavor), read(roles.formula), Boolean(roles.flavor));
    else this.apply(read('flavor', 'flavour'), read('formula'), true);
  };

  /**
   * The ground goes on .product-information, not on the scope. The event scope
   * is the ProductInformation- element, and the media gallery is measurably NOT
   * inside it — so a variable set there inherits to the details column only.
   * .product-information is the nearest ancestor holding both columns.
   */
  get groundTarget() {
    return this.closest('.product-information') || this.closest('main') || document.documentElement;
  }

  setGround(value) {
    const target = this.groundTarget;
    if (value) target.style.setProperty('--flavor-ground', value);
    else target.style.removeProperty('--flavor-ground');
  }

  apply(flavor, formula, hasFlavorAxis) {
    // Nothing read off the axis that identifies the panel. Leaving everything
    // alone beats clearing a ground or hiding a label that is still correct.
    if (hasFlavorAxis ? !flavor : !formula) return;

    if (hasFlavorAxis) {
      const ground = this.data.grounds?.[flavor];
      // A flavour with no ground clears it rather than keeping the last one, so
      // a draft or unwritten entry never borrows another flavour's colour.
      this.setGround(ground || '');
    }

    const label = this.data.labels?.[`${flavor}|${formula}`];
    const img = this.scope.querySelector?.('.pruvit-sf__img');
    if (img) {
      if (label) {
        img.hidden = false;
        if (img.getAttribute('src') !== label) img.setAttribute('src', label);
      } else {
        // No panel published for this combination. Hiding beats leaving the
        // previous flavour's label under the new flavour's name.
        img.hidden = true;
      }
    }
  }
}

if (!customElements.get('pruvit-variant-sync')) {
  customElements.define('pruvit-variant-sync', PruvitVariantSync);
}
